import { PayloadAction, createSlice } from '@reduxjs/toolkit';

export interface AgencyAllocator {
  id: string;
  allocatorId: string;
  status: string;
  allocatorType: string;
  totalAgents: number;
  allocatorName: string;
}

export interface AgencyAllocatorRequest {
  allocatorId: string;
  agencyId: string;
}

export interface ActivateAgencyAllocatorRequest {
  allocatorId: string;
  agencyId: string;
  vendorCode: string;
}

interface Allocator {
  id: string;
  allocatorName: string;
  city: string;
  state: string;
  pincode: string;
}

export interface AgencyAllocatorState {
  data: Allocator[] | null;
  loading: boolean;
  error: string | null;
  acceptedStatus: boolean | null;
  rejectedStatus: boolean | null;
  activateAgencyAllocatorStatus: boolean | null;
  activateAgencyAllocatorError: string | null;
}

export const agencyAllocatorSliceState: AgencyAllocatorState = {
  data: null,
  loading: false,
  error: null,
  acceptedStatus: null,
  rejectedStatus: null,
  activateAgencyAllocatorStatus: null,
  activateAgencyAllocatorError: null,
};

const agencyAllocatorSlice = createSlice({
  name: 'agencyAllocator',
  initialState: agencyAllocatorSliceState,
  reducers: {
    fetchAgencyAllocatorsRequest: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    fetchAgencyAllocatorsSuccess: (
      state,
      action: PayloadAction<Allocator[]>
    ) => {
      state.data = action.payload;
      state.loading = false;
    },
    fetchAgencyAllocatorsFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
    acceptInterestRequest: (
      state,
      action: PayloadAction<AgencyAllocatorRequest>
    ) => {
      state.loading = true;
      state.acceptedStatus = null;
    },
    acceptInterestSuccess: (state) => {
      state.loading = true;
      state.acceptedStatus = true;
    },
    acceptInterestFailure: (state) => {
      state.loading = true;
      state.acceptedStatus = false;
    },
    rejectInterestRequest: (
      state,
      action: PayloadAction<AgencyAllocatorRequest>
    ) => {
      state.loading = true;
      state.rejectedStatus = null;
    },
    rejectInterestSuccess: (state) => {
      state.loading = true;
      state.rejectedStatus = true;
    },
    rejectInterestFailure: (state) => {
      state.loading = true;
      state.rejectedStatus = false;
    },
    activateAgencyAllocatorRequest: (
      state,
      action: PayloadAction<ActivateAgencyAllocatorRequest>
    ) => {
      state.loading = true;
      state.activateAgencyAllocatorStatus = null;
      state.activateAgencyAllocatorError = null;
    },
    activateAgencyAllocatorSuccess: (state) => {
      state.loading = false;
      state.activateAgencyAllocatorStatus = true;
      state.activateAgencyAllocatorError = null;
    },
    activateAgencyAllocatorFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.activateAgencyAllocatorStatus = false;
      state.activateAgencyAllocatorError = action.payload;
    },
    resetAgencyAllocatorState: (state) => {
      state.data = null;
      state.loading = false;
      state.error = null;
      state.acceptedStatus = null;
      state.rejectedStatus = null;
      state.activateAgencyAllocatorStatus = null;
      state.activateAgencyAllocatorError = null;
    },
  },
});

export const {
  fetchAgencyAllocatorsRequest,
  fetchAgencyAllocatorsSuccess,
  fetchAgencyAllocatorsFailure,
  acceptInterestRequest,
  acceptInterestSuccess,
  acceptInterestFailure,
  rejectInterestRequest,
  rejectInterestSuccess,
  rejectInterestFailure,
  activateAgencyAllocatorRequest,
  activateAgencyAllocatorSuccess,
  activateAgencyAllocatorFailure,
  resetAgencyAllocatorState,
} = agencyAllocatorSlice.actions;

export default agencyAllocatorSlice.reducer;
